import "../global";
import { BlueConicProfileProperties } from "./BlueConicProfileProperties";
import { User } from "../gtm/User";
import { Logger } from "../utils/Logger";
import { BlueConicProfile } from "./blueConicJsApi/BlueConicProfile";
import { getOrCreateUserAdapter } from "../gtm/GtmUtils";
import { getCookie } from "typescript-cookie";
import { container } from "tsyringe";
import { SiteRules } from "../shared/SiteRules";
import { CookiesNames } from "../gtm/Cookies";
import { UieClient } from "../cbcUie/UieClient";
import { setOneYearCookie, validateBlueConicCookie } from "../utils/Cookies";
import { RcUserIdentifier } from "../shared/RcUserIdentifier";
import { GtmEventNames } from "../gtm/GtmEventNames";

/** Set up the BlueConic profile based on the state of the current user stored in the dataLayer.
 * @remarks
 * Warning: the profile is NOT ready until the callback is invoked!
 * @param caller Specifies the scope of the callback function.
 * @param callback - A function to call once the profile is loaded and the relevant identifier is set.
 * */
export default function setupProfile(caller: object, callback: () => void): void {
  const user = getOrCreateUserAdapter();
  if (user.cdpProfileId) {
    Logger.warn("Profile already setup");
    callback();
    return;
  }

  Logger.verbose("Started profile setup");
  Logger.log("Initial CDP profile Id:", blueConicClient.profile.getProfile().getId());
  blueConicClient.profile
    .getProfile()
    .loadValues(Object.values(BlueConicProfileProperties), caller, () => onPropertiesLoaded(caller, callback));
}

async function onPropertiesLoaded(caller: object, callback: () => void): Promise<void> {
  const profile = blueConicClient.profile.getProfile();
  const profileId = profile.getId();
  const profileRcId = profile.getValue(BlueConicProfileProperties.rcId);
  const profileRcVisitorId = profile.getValue(BlueConicProfileProperties.rcVisitorId);
  const isAnonymous = container.resolve<SiteRules>(SiteRules).isAnonymousUser;
  const dataLayerUser = getOrCreateUserAdapter();

  const userIdentifier = {
    rcId: dataLayerUser.rcId,
    rc_visitor_id: dataLayerUser.rc_visitor_id,
  } as RcUserIdentifier;
  if (profileRcId && profileRcVisitorId) {
    Logger.error(
      `[DSL] Profile ${profile.getId()} has both an RCID (${profileRcId}) and a RC_VisitorId (${profileRcVisitorId})!`,
    );
  }

  if (isCbcProfile(profile)) {
    Logger.log("CBC Profile. Creating a new profile.");
    if (isAnonymous) {
      userIdentifier.rc_visitor_id = await getRcVisitorId();
    }
    handleCreateProfile(caller, userIdentifier, callback, profileId);
    return;
  }

  if (!profileRcId && !profileRcVisitorId) {
    if (isAnonymous) {
      userIdentifier.rc_visitor_id = await getRcVisitorId();
    }
    const pageView = profile.getValue(BlueConicProfileProperties.totalPageViews);
    if (!pageView || parseInt(pageView) <= 1) {
      // Warning : this message is used in tests.
      Logger.verbose("New profile with zero or one page view. Using as is.");

      setRcIdOrVisitorId(caller, userIdentifier, callback);
      return;
    } else {
      Logger.verbose(
        `Used profile without IDs. Deleting and creating a new one. Old profile had ${pageView} page views`,
      );
      blueConicClient.profile.deleteProfile(caller, () => {
        handleCreateProfile(caller, userIdentifier, callback, profileId);
      });
      return;
    }
  }

  if (isAnonymous && profileRcVisitorId) {
    Logger.log("Anonymous user with Profile RCVisitorId. Using RCVisitorID from CDP :", profileRcVisitorId);
    updateRcVisitorBackup(profileRcVisitorId);
    userIdentifier.rc_visitor_id = profileRcVisitorId;
    profileSetupCompleted(caller, userIdentifier, false, callback);
    return;
  }

  if (isAnonymous && profileRcId) {
    Logger.log("Anonymous user with Profile RCId. Creating new profile");
    userIdentifier.rc_visitor_id = await getRcVisitorId();
    handleCreateProfile(caller, userIdentifier, callback, profileId);
    return;
  }

  if (userIdentifier.rcId && profileRcVisitorId) {
    Logger.log("User with RCID with Profile RCVisitorId. Creating new profile");
    updateRcVisitorBackup(profileRcVisitorId);
    handleCreateProfile(caller, userIdentifier, callback, profileId);
    return;
  }

  if (profileMatchCurrentUser(userIdentifier, profileRcId)) {
    Logger.log("Profile match the current user.");
    profileSetupCompleted(caller, userIdentifier, false, callback);
    return;
  } else {
    Logger.log("User and Profile RCID don't match, creating another profile");
    handleCreateProfile(caller, userIdentifier, callback, profileId);
  }
}

/**
 * Saving the rcVisitorId for future uses, since the rcVisitor cookie is deleted if a user is connected
 */
function updateRcVisitorBackup(rcVisitorId: string): void {
  setOneYearCookie(CookiesNames.rcVisitorBackup, rcVisitorId);
}

async function getRcVisitorId(): Promise<string | undefined> {
  let rcVisitorId = getCookie(CookiesNames.rcVisitorBackup);
  if (rcVisitorId) {
    // Refresh cookie
    updateRcVisitorBackup(rcVisitorId);
    Logger.log(`Using RcVisitorId from ${CookiesNames.rcVisitorBackup}: `, rcVisitorId);
  } else {
    Logger.verbose("RCVisitorId not found in visitor backup cookie");
    const uieClient = container.resolve<UieClient>(UieClient);
    rcVisitorId = await uieClient.handleRcVisitorId();
    if (!rcVisitorId) {
      Logger.error("handleRcVisitorId didn't return a RCVisitorId!");
    }
  }

  return rcVisitorId;
}

function isCbcProfile(profile: BlueConicProfile): boolean {
  return (
    !!profile.getValue(BlueConicProfileProperties.cbcPlusId) ||
    !!profile.getValue(BlueConicProfileProperties.cbcVisitorId)
  );
}

function profileMatchCurrentUser(user: RcUserIdentifier, profileRcId?: string): boolean {
  return !!(user.rcId && user.rcId === profileRcId);
}

function handleCreateProfile(
  caller: object,
  userIdentifier: RcUserIdentifier,
  callback: () => void,
  oldProfileId: string,
): void {
  if (!userIdentifier.rcId && !userIdentifier.rc_visitor_id) {
    Logger.error("No user identifier available to create a new profile.");
    return;
  }
  blueConicClient.profile.createProfile(caller, () => {
    const newProfileId = blueConicClient.profile.getProfile().getId();
    if (newProfileId === oldProfileId) {
      Logger.error(`Post Create Profile, ProfileId is unchanged: ${newProfileId}`);
    } else Logger.log("Post Create Profile ProfileId:", newProfileId);
    // Call suggested by BlueConic: triggers a page view event to increase the chance of switching to the new profile.
    // Also needed if we previously deleted the profile loaded by the page.
    // noinspection SpellCheckingInspection
    blueConicClient.createEvent("PAGEVIEW", null, caller, function () {
      const profile = blueConicClient.profile.getProfile();
      Logger.log("PageView ProfileId:", profile.getId());
      setRcIdOrVisitorId(caller, userIdentifier, callback);
    });
  });
}

function setRcIdOrVisitorId(caller: object, user: RcUserIdentifier, callback: () => void): void {
  if (user.rcId) {
    setRcIdInProfile(caller, user.rcId, callback);
  } else if (user.rc_visitor_id) {
    setRcVisitorIdInProfile(caller, user.rc_visitor_id, callback);
  } else {
    Logger.error("No user identifier available!");
  }
}

function setRcIdInProfile(caller: object, rcId: string, callback: () => void): void {
  Logger.debugLog(`Setting RCID ${rcId} on profile ${blueConicClient.profile.getProfile().getId()}`);
  blueConicClient.profile.getProfile().setValue(BlueConicProfileProperties.rcId, rcId);
  blueConicClient.profile.updateProfile(caller, () => {
    const profile = blueConicClient.profile.getProfile();
    Logger.debugLog(`Post Update : RCID ${rcId} set on profile ${profile.getId()}`);
    profileSetupCompleted(caller, { rcId: rcId, rc_visitor_id: undefined }, true, callback);
  });
}

function setRcVisitorIdInProfile(caller: object, rcVisitorId: string, callback: () => void): void {
  Logger.debugLog(`Setting rc_visitor_id ${rcVisitorId} on profile ${blueConicClient.profile.getProfile().getId()}`);
  blueConicClient.profile.getProfile().setValue(BlueConicProfileProperties.rcVisitorId, rcVisitorId);
  blueConicClient.profile.updateProfile(caller, () => {
    const profile = blueConicClient.profile.getProfile();
    Logger.debugLog(`Post Update rc_visitor_id ${rcVisitorId} ProfileId ${profile.getId()}`);
    profileSetupCompleted(caller, { rcId: undefined, rc_visitor_id: rcVisitorId }, true, callback);
  });
}

function profileSetupCompleted(
  caller: object,
  userIdentifiers: RcUserIdentifier,
  needReload: boolean,
  callback: () => void,
): void {
  if (needReload) {
    // This reload of the profile's properties is required to set the postal code / FSA group IDs because
    // the frequent postal code property or declared postal code could have been merged from another profile
    // after the user's identifier is set.
    Logger.verbose("Reloading profile after setting RCID or RCVisitorId");

    blueConicClient.profile
      .getProfile()
      .loadValues(Object.values(BlueConicProfileProperties), caller, () =>
        profileSetupCompleted({}, userIdentifiers, false, callback),
      );
    return;
  }
  const profile = blueConicClient.profile.getProfile();
  const user: User = {
    cdpProfileId: profile.getId(),
    rc_visitor_id: userIdentifiers.rc_visitor_id,
  };
  dataLayerQueue.push({
    event: GtmEventNames.CdpProfileSetupCompleted,
    user: user,
  });

  const profileRcId = profile.getValue(BlueConicProfileProperties.rcId);
  const profileRcVisitorId = profile.getValue(BlueConicProfileProperties.rcVisitorId);
  Logger.log(
    `profileSetupCompleted: ProfileID: ${profile.getId()} Profile RCID: ${profileRcId} Profile rcVisitorId: ${profileRcVisitorId}`,
  );

  if (profileRcId && profileRcVisitorId) {
    Logger.error(
      `[ERROR] Profile ${profile.getId()} has both an RCID (${profileRcId}) and a RC_VisitorId (${profileRcVisitorId})!`,
    );
  }
  if (!profileRcId && !profileRcVisitorId) {
    Logger.error(`Profile ${profile.getId()} doesn't have any ID assigned!`);
  }
  validateBlueConicCookie();
  callback();
}
